import { BusinessHolidayHoursDto, BusinessHoursDto, MenuHoursDto, MerchantDto } from '@up/data';
import { timeToDate } from '../extensions/date.helper';

export function isBusinessOpen(merchant: MerchantDto): boolean {
  let isOpen = false;
  const currentDate = new Date();

  for (const businessHours of merchant.businessHours) {
    if (
      !businessHours.isOverNight &&
      businessHours.weekday === (currentDate.getUTCDay() + 6) % 7
    ) {
      isOpen = businessHours.isOpen;
    }
  }
  return isOpen;
}

export function getBusinessHolidayForDate(
  businessHolidayHours: BusinessHolidayHoursDto[],
  date?: Date
): BusinessHolidayHoursDto | null {
  if (!businessHolidayHours || businessHolidayHours.length === 0) return null;

  const effectiveDate = date || new Date();
  const currentDateStr = effectiveDate.toDateString();
  
  return businessHolidayHours.find(
    (bhh) => new Date(bhh.holidayDate).toDateString() === currentDateStr
  );
}

export function getBusinessHoliday(
  businessHolidayHours: BusinessHolidayHoursDto[],
  date: Date
) {
  if (!businessHolidayHours || businessHolidayHours.length === 0) return null;

  const currentDate = date.toDateString();
  return businessHolidayHours.find(
    (bhh) => new Date(bhh.holidayDate).toDateString() === currentDate
  );
}

export function isBusinessOpenOnHoliday(
  businessHolidayHours: BusinessHolidayHoursDto[], 
  date?: Date
): boolean {
  if (!businessHolidayHours || businessHolidayHours.length === 0) return true;

  const holiday = getBusinessHolidayForDate(businessHolidayHours, date);

  if (holiday) {
    return holiday.isOpen;
  } else {
    return true;
  }
}

export function isBusinessOpenWithinHoliday(
  merchant: MerchantDto,
  openingTime: Date,
  closingTime: Date,
  date?: Date
): boolean {
  const { businessHolidayHours } = merchant;

  if (!businessHolidayHours || businessHolidayHours.length === 0) return true;

  if(!openingTime || !closingTime) return false;

  const holiday = getBusinessHolidayForDate(businessHolidayHours, date);

  if (holiday) {
    const openingTimeBusinessHolidayHour = getMerchantBusinessHolidayStartTimeForDate(merchant, date);
    const closingTimeBusinessHolidayHour = getMerchantBusinessHolidayEndTimeForDate(merchant, date);

    const opensDuringHoliday = openingTime.getTime() < closingTimeBusinessHolidayHour.getTime();
    const closesDuringHoliday = closingTime.getTime() > openingTimeBusinessHolidayHour.getTime();

    return opensDuringHoliday && closesDuringHoliday;
  } else {
    return true;
  }
}

export function isBusinessOpenWithinHolidayV2(
  businessHolidayHours: BusinessHolidayHoursDto,
  date: Date
): boolean {
  if (businessHolidayHours) {
    const openingTimeBusinessHolidayHour = timeToDate(businessHolidayHours.startTime, true, date);
    const closingTimeBusinessHolidayHour = timeToDate(businessHolidayHours.endTime, true, date);

    const opensDuringHoliday = date.getTime() < closingTimeBusinessHolidayHour.getTime();
    const closesDuringHoliday = date.getTime() > openingTimeBusinessHolidayHour.getTime();

    return opensDuringHoliday && closesDuringHoliday;
  } else {
    return true;
  }
}

export function getMerchantBusinessHolidayStartTimeForDate(
  merchant: MerchantDto, 
  date?: Date
): Date | null {
  const holiday = getBusinessHolidayForDate(merchant.businessHolidayHours, date);

  if (holiday && holiday.isOpen) {
    return timeToDate(holiday.startTime, true, date);
  }

  return null;
}

export function getMerchantBusinessHolidayEndTimeForDate(
  merchant: MerchantDto, 
  date?: Date
): Date | null {
  const holiday = getBusinessHolidayForDate(merchant.businessHolidayHours, date);

  if (holiday && holiday.isOpen) {
    const endTime = timeToDate(holiday.endTime, true, date);

    if (endTime.getHours() === 0) {
      endTime.setDate(endTime.getDate() + 1);
    }

    return endTime;
  }

  return null;
}

export function getMerchantBusinessStartTimeForDay(
  merchant: MerchantDto,
  date: Date
): Date {
  for (const businessHours of merchant.businessHours) {
    if (
      !businessHours.isOverNight &&
      businessHours.weekday === (date.getUTCDay() + 6) % 7 &&
      businessHours.isOpen
    ) {
      return timeToDate(businessHours.startTime, true, date);
    }
  }
}

export function getMerchantBOStartEndTimeForNextWeek(
  businessHours: BusinessHoursDto[]
) {
  const currentDate = new Date();
  const weekAhead = [
    currentDate,
    new Date(currentDate.getTime() + 24 * 60 * 60 * 1000),
    new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 * 2),
    new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 * 3),
    new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 * 4),
    new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 * 5),
    new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 * 6)
  ];

  return weekAhead.map((date) => {
    const dayOfWeek = (date.getUTCDay() + 6) % 7;
    const businessHoursForDay = businessHours.find(
      (bh) => bh.weekday === dayOfWeek
    );

    const formatedStartTime = businessHoursForDay.isOpen ? formatTime(timeToDate(businessHoursForDay.startTime, true)) : '';
    const formatedEndTime = businessHoursForDay.isOpen ? formatTime(timeToDate(businessHoursForDay.endTime, true)) : '';
    const extraMsg = formatedStartTime > formatedEndTime && formatedEndTime !== '00:00' ? '(next day)' : '';

    const timeIntervals: {
      startTime: string,
      endTime: string,
      extraMsg?: string
    }[] = [
      {
        startTime: formatedStartTime,
        endTime: formatedEndTime,
        extraMsg: businessHoursForDay.isOpen ? extraMsg : 'Closed'
      }
    ]

    return {
      dayOfWeek: dayOfWeek,
      timeIntervals
    };
  });
}

export function getBusinessHoursOverNightStartTimeForDay(
  businessHours: BusinessHoursDto,
  currentDate: Date
): Date {
  if (businessHours.isOpen) {
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;
    if (
      //check for the monday weekday and businessHours for sunday weekday
      (weekDayNow === 0 && businessHours.weekday === 6) ||
      //check for the previous weekday, monday not checked
      (weekDayNow !== 0 && weekDayNow - 1 === businessHours.weekday)
    ) {
      const startTime = timeToDate(businessHours.startTime, true, currentDate);
      startTime.setUTCDate(startTime.getUTCDate() - 1);
      return startTime;
    }

    if (
      //check for the monday weekday with businessHours
      (weekDayNow === 0 && businessHours.weekday === 0) ||
      //check for the current day
      weekDayNow === businessHours.weekday
    ) {
      return timeToDate(businessHours.startTime, true, currentDate);
    }
  }
}


export function getMerchantBusinessEndTimeForDay(
  merchant: MerchantDto,
  date: Date
): Date {
  for (const businessHours of merchant.businessHours) {
    if (
      !businessHours.isOverNight &&
      businessHours.weekday === (date.getUTCDay() + 6) % 7 &&
      businessHours.isOpen
    ) {
      const endTime = timeToDate(businessHours.endTime, true, date);

      if (endTime.getHours() === 0) {
        endTime.setDate(endTime.getDate() + 1);
      }

      return endTime;
    }
  }
}

export function getBusinessHoursOverNightEndTimeForDay(
  businessHour: BusinessHoursDto,
  currentDate: Date,
): Date {
  if (businessHour.isOpen) {
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;
    let addDay = false;
    
    if (
      (weekDayNow === 0 && businessHour.weekday === 6) ||
      (weekDayNow === 0 && businessHour.weekday === 0) ||
      weekDayNow === businessHour.weekday
    ) {
      addDay = true;
    }

    const endTimeDate = timeToDate(businessHour.endTime, true, currentDate);
    
    if (addDay) {
      endTimeDate.setUTCDate(endTimeDate.getUTCDate() + 1);
    }
    
    return endTimeDate;
  }
}

export function getBusinessHoursOverNightEndTimeForPreviousDay(
  businessHour: BusinessHoursDto,
  currentDate: Date
): Date {
  if (businessHour.isOpen) {
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;
    let addDay = false;

    if (
      //check for the current day
      weekDayNow !== businessHour.weekday
    ) {
      addDay = true;
    }

    const endTimeDate = timeToDate(businessHour.endTime, addDay);

    if (businessHour.endTime.hours < businessHour.startTime.hours && !addDay) {
      endTimeDate.setUTCDate(endTimeDate.getUTCDate() + 1);
    }

    return endTimeDate;
  }
}

export function getBusinessHoursOverNight(merchant: MerchantDto, currentDate: Date) {
  const businessHourOverNight = [];
  const currentDateUTCDay = currentDate.getUTCDay();
  const previousDay = (currentDateUTCDay + 6) % 7;

  merchant?.businessHours?.forEach(businessHour => {
      if (businessHour.isOverNight && businessHour.isOpen) {
          if (businessHour.weekday === currentDateUTCDay || businessHour.weekday === previousDay) {
              businessHourOverNight.push(businessHour);
          }
      }
  });

  return businessHourOverNight;
}

export function getBusinessHoursOverNightPreviousDay(
  merchant: MerchantDto,
  currentDate: Date
) {
  const businessHourOverNight = [];
  const currentDateUTCDay = currentDate.getUTCDay();
  const weekDayNow = (currentDateUTCDay + 6) % 7;

  const currentDayBusinessHour = merchant?.businessHours.find(
    (businessHour) =>
      businessHour.isOverNight &&
      businessHour.isOpen &&
      //check for the current day
      weekDayNow === businessHour.weekday
  );

  if (currentDayBusinessHour) {
    const currentDayEndTime = timeToDate(
      currentDayBusinessHour.endTime,
      true
    );
    currentDayEndTime.setUTCDate(
      currentDayEndTime.getUTCDate() + 1
    );

    if (currentDayEndTime > currentDate)
      businessHourOverNight.push(currentDayBusinessHour);
  }

  return businessHourOverNight;
}

export function checkForValidBusinessHourOverNight(
  businessHoursOverNight: BusinessHoursDto[],
  menuHoursOverNight: MenuHoursDto[],
  currentDate: Date,
  todayOvernight?: boolean
): BusinessHoursDto {
  let validBusinessHour;
  const currentDateUTCDay = currentDate.getUTCDay();
  const weekDayNow = (currentDateUTCDay + 6) % 7;
  const sundayBusinessHour = businessHoursOverNight.find(
    (x) => x.weekday === 6 && weekDayNow === 0
  );
  const sundayMenuHour = menuHoursOverNight.find(
    (x) => x.weekday === 6 && weekDayNow === 0
  );
  if (sundayBusinessHour && sundayMenuHour) {
    validBusinessHour = sundayBusinessHour;
  } else {
    const findValidHours = (x, businessHour) =>
      todayOvernight
        ? (x.weekday + 1) === businessHour.weekday
        : x.weekday === businessHour.weekday;

    businessHoursOverNight.forEach((businessHour) => {
      if (!validBusinessHour && menuHoursOverNight.find((x) => findValidHours(x, businessHour))
      ) {
        validBusinessHour = businessHour;
      }
    });
  }
  return validBusinessHour;
}


export function padTo2Digits(num: number): string {
  return num.toString().padStart(2, '0');
}

export function formatTime(date: Date): string {
  if (!date) return '';
  return [
    padTo2Digits(date.getHours()),
    padTo2Digits(date.getMinutes())
  ].join(':');
}